<template>
  <div
    v-if="loading || error"
    class="row justify-center items-center"
  >
    <div class="col-auto">
      <div
        v-if="error"
        class="text-center"
      >
        <q-icon
          color="negative"
          name="info"
        />
        Error occurred <br>
        <q-btn
          icon="refresh"
          label="Try again"
          class="q-mt-xs"
          @click="init(true)"
        />
      </div>
      <q-spinner-puff
        v-else-if="loading"
        color="primary"
        size="60px"
      />
    </div>
  </div>
  <div
    v-else
    class="row gutter-sm"
  >
    <bj-crud-form-modal
      ref="crudModal"
      :fields="fields"
      :edit-fields="editFields"
      :date-fields="dateFields"
      model="User"
      endpoint-create="admin/agents"
      endpoint-edit="users"
      :callback="beforeSave"
      not-crud
      @saved="onSave"
    />
    <send-notification-modal
      v-if="sendNotificationModal.visible"
      :send-notification-modal="sendNotificationModal"
      @close="sendNotificationModal.visible = false"
    />
    <div class="col-12">
      <q-card>
        <q-card-main>
          <div class="row gutter-md items-center">
            <div class="col-auto">
              <q-chip
                v-if="agent.is_showami"
                class="font-weight-bold"
                style="font-size: 2rem; padding: 1rem;"
                color="orange"
                text-color="white"
              >
                Showami
              </q-chip>
              <img
                v-else
                id="avatar"
                :src="agent.avatar_file_url"
                class="float-left"
              >
            </div>
            <div class="col">
              <div
                id="full-name"
                class="q-mb-xs d-flex align-items-center"
              >
                <span>{{ agent.full_name }}</span>
                <span class="mx-3">|</span>
                <span>{{ agent.email }}</span>
                <SdCopyToClipboard
                  with-icon
                  :copy="agent.email"
                  class="ml-2"
                />
                <span class="mx-3">|</span>
                <span>{{ agent.formatted_phone_number }}</span>
                <SdCopyToClipboard
                  with-icon
                  :copy="agent.phone_number"
                  class="ml-2"
                />
              </div>
              <span>System ID:{{ agent.id }}</span>
              <div v-if="agent.device">
                {{ agent.first_name }} is using our App with {{ agent.device.model }} (version:{{ agent.app_version }})
              </div>
              <a
                v-if="agent.location"
                :href="`https://www.google.com/maps/search/?api=1&query=${agent.location.coordinates[1]},${agent.location.coordinates[0]}`"
                target="_blank"
              >
                Click to open last known location
              </a>
              <div v-if="agent.location_updated_at">
                Location last updated at: {{ moment(agent.location_updated_at).tz(moment.tz.guess()).format('MM/DD/YY h:mmA') }}
              </div>
              <div v-if="agent.used_app_at">
                Last used app at: {{ moment(agent.used_app_at).tz(moment.tz.guess()).format('MM/DD/YY h:mmA') }}
              </div>
              <div>
                Joined at {{ moment(agent.created_at).tz(moment.tz.guess()).format('MM/DD/YY h:mmA') }}
              </div>
              <div class="mt-2 mb-2">
                Markets: {{ agent.markets.map(market => market.name).join(', ') }}
              </div>
              <div
                v-if="agent.contact_status"
                class="mt-2 mb-2"
              >
                Greeting call: {{ resolveContactStatus(agent.contact_status) }}
              </div>
              <div
                v-if="agent.contact_status_updated_at"
                class="mt-2 mb-2"
              >
                Greeting call updated at: {{ moment(agent.contact_status_updated_at).format('MM/DD/YY') }}
              </div>
              <div
                class="mt-2 mb-2"
                :class="agent.is_periodic_inspection_capable ? 'text-positive' : 'text-negative'"
              >
                <q-icon :name="agent.is_periodic_inspection_capable ? 'check' : 'close'" />
                {{ agent.is_periodic_inspection_capable ? 'Periodic inspections capable' : 'Periodic inspections incapable' }}
              </div>
              <div
                class="mt-2 mb-2"
                :class="agent.is_mimo_inspection_capable ? 'text-positive' : 'text-negative'"
              >
                <q-icon :name="agent.is_mimo_inspection_capable ? 'check' : 'close'" />
                {{ agent.is_mimo_inspection_capable ? 'Move-in/Out inspections capable' : 'Move-in/Out inspections incapable' }}
              </div>
              <div
                class="mt-2 mb-2"
                :class="agent.is_vacancy_check_inspection_capable ? 'text-positive' : 'text-negative'"
              >
                <q-icon :name="agent.is_vacancy_check_inspection_capable ? 'check' : 'close'" />
                {{ agent.is_vacancy_check_inspection_capable ? 'Vacancy check inspections capable' : 'Vacancy check inspections incapable' }}
              </div>
              <div
                class="mt-2 mb-2"
                :class="agent.is_marketing_photos_capable ? 'text-positive' : 'text-negative'"
              >
                <q-icon :name="agent.is_marketing_photos_capable ? 'check' : 'close'" />
                {{ agent.is_marketing_photos_capable ? 'Marketing Photos capable' : 'Marketing Photos capable incapable' }}
              </div>
              <div
                v-if="agent.is_tours_capable"
                class="mt-2 mb-2 text-positive"
              >
                <q-icon name="check" />
                Tours - Capable
              </div>
              <div
                v-if="agent.is_tours_experienced"
                class="mt-2 mb-2 text-positive"
              >
                <q-icon name="check" />
                Tours - Experienced
              </div>
              <div
                v-if="agent.is_inspections_experienced"
                class="mt-2 mb-2 text-positive"
              >
                <q-icon name="check" />
                Inspections - Experienced
              </div>
              <div>
                <q-rating
                  v-model="agent.rating"
                  readonly
                />
                ({{ agent.reviews_count }} reviews)
              </div>
              <div
                v-if="agent.muted_until"
                class="q-mt-sm"
              >
                <q-chip
                  small
                  color="negative"
                >
                  Muted until
                  {{ moment(agent.muted_until).format('MM/DD/YY h:mmA') }}
                </q-chip>
              </div>
              <div v-if="agent.admin_notes">
                <span class="q-card-title">Admin notes</span>
                <p
                  class="pre-line"
                  v-html="agent.admin_notes"
                />
              </div>
              <div class="q-mt-sm">
                <q-btn
                  v-if="agent.stripe != null"
                  size="sm"
                  rounded
                  color="blue"
                  outline
                  icon="fab fa-cc-stripe"
                  label="Payment Dashboard"
                  no-caps
                  @click="openStripeAcc"
                />
              </div>
              <div class="q-mt-sm">
                <q-btn
                  v-if="!agent.verified"
                  :disable="disabled"
                  size="sm"
                  rounded
                  outline
                  color="positive"
                  icon="mail"
                  no-caps
                  label="Resend Invite"
                  @click.native="sendVerificationEmail"
                />
              </div>
              <div class="q-mt-sm">
                <q-btn
                  size="sm"
                  rounded
                  outline
                  color="blue"
                  no-caps
                  label="Edit Agent"
                  @click.native="openEditModal"
                />
              </div>
              <div class="q-mt-sm">
                <q-btn
                  :disable="disabled"
                  size="sm"
                  rounded
                  :color="agent.blocked ? 'green' : 'red'"
                  outline
                  :label="agent.blocked ? 'Unblock Agent': 'Block Agent'"
                  no-caps
                  @click="blockAgent"
                />
              </div>
              <div
                v-if="!agent.is_showami"
                class="q-mt-sm"
              >
                <q-btn
                  size="sm"
                  rounded
                  outline
                  color="positive"
                  label="Send push notification"
                  no-caps
                  @click.native="sendNotificationModal.visible = true"
                />
              </div>
              <div
                v-if="!agent.is_showami && (agent.is_mimo_inspection_capable || agent.is_periodic_inspection_capable || agent.is_vacancy_check_inspection_capable)"
                class="q-mt-sm"
              >
                <q-btn
                  :disable="disabled"
                  size="sm"
                  rounded
                  outline
                  color="positive"
                  label="Send inspection briefing for tomorrow"
                  no-caps
                  @click.native="sendInspectionsBriefing()"
                />
              </div>
            </div>
          </div>
        </q-card-main>
      </q-card>
    </div>
    <div class="col-md-6 col-xs-12">
      <q-card>
        <q-card-title>
          Earnings
          <q-chip slot="right">
            ${{ agent.transactions_sum }}
          </q-chip>
        </q-card-title>
      </q-card>
    </div>
    <div class="col-md-6 col-xs-12">
      <q-card>
        <q-card-title>
          Last 7 days Earnings
          <q-chip slot="right">
            ${{ agent.last_week_transactions_sum }}
          </q-chip>
        </q-card-title>
      </q-card>
    </div>
    <div class="col-md-6 col-xs-12">
      <q-card>
        <q-card-title>
          Tours count
          <q-chip slot="right">
            {{ agent.tours_count }}
          </q-chip>
        </q-card-title>
      </q-card>
    </div>
    <div class="col-md-6 col-xs-12">
      <q-card>
        <q-card-title>
          Last 7 days tours count
          <q-chip slot="right">
            {{ agent.last_week_tours_count }}
          </q-chip>
        </q-card-title>
      </q-card>
    </div>
    <div class="col-md-6 col-xs-12">
      <q-card>
        <q-card-title>
          <router-link
            v-tippy
            :to="{ name: 'app.inspections', query: { 'agents[]': agent.id } }"
            title="View agent's inspections"
          >
            Inspections
          </router-link> count
          <q-chip slot="right">
            {{ agent.inspections_count }}
          </q-chip>
        </q-card-title>
      </q-card>
    </div>
    <!--<div class="col-12">-->
    <!--@TODO Display of total payout, weekly payout, and more....-->
    <!--@TODO Extra data: tours count, tours count in the last week, tours stats (completed, technical issue, cancelled...)-->
    <!--</div>-->
    <div class="col-12">
      <bj-tours-data-table
        :with-header="false"
        :with-agent-filter="false"
        :filter-by-agent-id="agent.id"
      />
    </div>
  </div>
</template>

<script>
import DateTime from '@/plugins/bluejay/DateTime';
import config from '@/plugins/bluejay/config';
import SdCopyToClipboard from '@/components/common/SdCopyToClipboard.vue';
import BjToursDataTable from '../../components/tours-data-table';
import SendNotificationModal from '../../plugins/bluejay/components/modals/send-notification-modal';
import SdDatetime from '../../plugins/bluejay/components/datetime';

const { contactStatuses } = config;
export default {
  components: {
    SdCopyToClipboard,
    SdDatetime,
    SendNotificationModal,
    BjToursDataTable,
  },
  data() {
    return {
      agent: null,
      loading: true,
      error: false,
      sendNotificationModal: {
        visible: false,
        user_id: null,
      },
      contactStatuses,
      stripe_url: '',
      markets: [],
      fields: [
        {
          name: 'first_name',
          type: 'text',
          label: 'First Name',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'last_name',
          type: 'text',
          label: 'Last Name',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'email',
          type: 'email',
          label: 'Email',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'phone_number',
          type: 'text',
          label: 'Phone Number',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'markets',
          type: 'chips-autocomplete',
          label: 'Markets',
          class: 'col-xs-12',
          placeholder: 'Add a market...',
          search: (query) => this.markets
            .filter((market) => market.name.toLowerCase().includes(query.toLowerCase())),
          toLabel: (market) => `${market.name} #${market.id}`,
        },
        {
          name: 'contact_status',
          type: 'select',
          label: 'Greeting Call',
          class: 'col-xs-12 col-md-6',
          clearable: true,
          optionsResolver: () => Promise.resolve(Object.values(contactStatuses).map((contactStatus) => ({
            label: contactStatus.label,
            value: contactStatus.value,
          }))),
        },
        {
          name: 'contact_status_updated_at',
          type: 'date',
          label: 'Greeting call updated at',
          class: 'col-xs-12 col-md-6',
          utc: true,
          clearable: true,
        },
        {
          name: 'is_periodic_inspection_capable',
          type: 'toggle',
          label: 'Periodic inspections capable',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'is_mimo_inspection_capable',
          type: 'toggle',
          label: 'Move-in/Out inspections capable',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'is_vacancy_check_inspection_capable',
          type: 'toggle',
          label: 'Vacancy check inspections capable',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'is_marketing_photos_capable',
          type: 'toggle',
          label: 'Marketing Photos capable',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          class: 'col-xs-12',
        },
        {
          name: 'is_tours_experienced',
          type: 'toggle',
          label: 'Tours Experience',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'is_inspections_experienced',
          type: 'toggle',
          label: 'Inspections Experience',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'is_tours_capable',
          type: 'toggle',
          label: 'Tours capable',
          rules: ['required'],
          class: 'col-xs-12 col-md-6',
        },
        {
          name: 'muted_until',
          type: 'date',
          label: 'Muted until',
          class: 'col-xs-12 col-md-6',
          clearable: true,
          min: new Date(),
          utc: true,
        },
        {
          name: 'admin_notes',
          type: 'custom-textarea',
          label: 'Notes',
          class: 'col-12',
          placeholder: 'Enter notes regarding agent',
        },
        {
          name: 'is_ops_internal_agent',
          type: 'toggle',
          label: 'Is Ops Internal Agent? (agent without payouts)',
          class: 'col-12 mt-5 text-red',
        },
      ],
      dateFields: ['muted_until'],
      disabled: false,
    };
  },
  computed: {
    editFields() {
      const isShowami = Boolean(this.agent && this.agent.is_showami);
      return this.fields.map((field) => {
        if (field.name === 'markets' && isShowami) {
          return { ...field, condition: () => false };
        }
        return field;
      });
    },
  },
  created() {
    this.init();
    this.fetchMarkets();
  },
  methods: {
    init(refresh) {
      if (refresh) {
        this.loading = true;
        this.error = false;
      }

      this.fetchAgent(this.$route.params.id).then((agent) => {
        this.agent = agent;
        this.sendNotificationModal.user_id = agent.id;
        if (this.agent.stripe) {
          this.connectToStripe(this.agent.id);
        }

        if (this.agent.muted_until) {
          this.agent.muted_until = this.moment(this.agent.muted_until)
            .tz(this.agent.markets.length ? this.agent.markets[0].timezone : null)
            .format(DateTime.serverFormat);
        }

        this.loading = false;
      }).catch((error) => {
        this.error = error;
      });
    },
    fetchAgent(id) {
      return this.api().request('GET', `admin/agents/${id}`);
    },
    fetchMarkets() {
      this.api().request('GET', 'admin/markets', {}, {
        paginate: 0,
      }).then((markets) => this.markets = markets);
    },
    openEditModal() {
      const toggleFieldNames = [
        'is_periodic_inspection_capable',
        'is_mimo_inspection_capable',
        'is_vacancy_check_inspection_capable',
        'is_marketing_photos_capable',
        'is_tours_experienced',
        'is_inspections_experienced',
        'is_tours_capable',
        'is_ops_internal_agent',
      ];
      const payload = { ...this.agent, markets: this.agent.markets || [] };
      toggleFieldNames.forEach((name) => {
        if (payload[name] == null) {
          payload[name] = false;
        }
      });
      this.$refs.crudModal.open('edit', payload);
    },
    beforeSave(payload) {
      const cleanPayload = {
        id: payload.id,
      };
      const fieldsToUse = this.agent && this.agent.is_showami ? this.editFields : this.fields;
      fieldsToUse.forEach((field) => {
        if (field.name) cleanPayload[field.name] = payload[field.name];
      });
      const markets = cleanPayload.markets || [];
      cleanPayload.markets = markets.map((market) => (typeof market === 'object' && market && 'id' in market ? market.id : market));

      return cleanPayload;
    },
    onSave() {
      this.init(false);
    },
    async connectToStripe(user_id) {
      await this.api().request('POST', 'stripe/connect/login-link', {
        user_id,
      }).then((response) => {
        this.stripe_url = response.url;
      }).catch(() => {});
    },
    blockAgent() {
      const url = `${this.agent.blocked ? 'un' : ''}block-agent/${this.agent.id}`;
      this.disabled = true;
      this.api().request('POST', url).then(() => {
        this.agent.blocked = !this.agent.blocked;
        const agent_state = this.agent.blocked ? 'blocked' : 'unblocked';
        this.uiNotify(`Agent ${agent_state} successfully`, 'positive');
      }).catch(() => {
        this.uiNotify('Error blocking agent', 'negative');
      })
        .finally(() => {
          this.disabled = false;
        });
    },
    deleteAgent() {
      this.uiConfirm(
        'Are you sure you want to delete this Agent ?',
      ).then(() => {
        this.api()
          .request('DELETE', `users/${this.agent.id}`)
          .then(() => {
            this.uiNotify(`Agent ${this.agent.full_name} was deleted`);
            this.redirectBack();
          });
      });
    },
    sendVerificationEmail() {
      this.disabled = true;
      this.uiConfirm('Are you sure?').then(() => {
        this.api().request('POST', `users/${this.agent.id}/send-invitation-mail`).then(() => {
          this.uiNotify('Verification email sent.');
        }).catch(() => {
          this.uiNotify('Error', 'negative');
        })
          .finally(() => {
            this.disabled = false;
          });
      });
    },
    openStripeAcc() {
      this.openExternalBrowser(this.stripe_url);
    },
    sendInspectionsBriefing() {
      this.disabled = true;
      this.$q.dialog({
        title: 'Send inspection briefing for tomorrow?',
        ok: 'Confirm',
        cancel: 'Cancel',
      }).then(() => {
        this.api().request('POST', `admin/agents/${this.agent.id}/inspections/brief`).then(() => {
          this.uiNotify('Success');
        }).catch((error) => {
          this.uiNotify(error.message || 'Error occurred', 'negative');
        })
          .finally(() => {
            this.disabled = false;
          });
      });
    },
    resolveContactStatus(status) {
      return contactStatuses[status].label;
    },
  },
};
</script>

<style lang="scss">
  @import '../../css/common';

  #avatar {
    width: 100px;
  }

  #full-name {
    font-size: 24px;
  }
</style>
